import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Stack,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Snackbar,
} from '@mui/material';
import { auth } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Theme } from '@mui/material/styles';

interface Profile {
    name: string;
    email: string;
    phone: string;
    username: string;
    created_at: string;
}

export const ProfilePage: React.FC = () => {
    document.title = "Duitleh - Profile";
    const { logout } = useAuth();
    const [profile, setProfile] = useState<Profile | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        email: '',
        phone: '',
    });
    const [changePasswordForm, setChangePasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        try {
            const response = await auth.getProfile();
            setProfile(response.data);
            setEditForm({
                name: response.data.name,
                email: response.data.email,
                phone: response.data.phone || '',
            });
        } catch (error) {
            console.error('Failed to load profile:', error);
            showSnackbar('Failed to load profile', 'error');
        }
    };

    const handleEditSubmit = async () => {
        try {
            await auth.updateProfile(editForm);
            setIsEditing(false);
            loadProfile();
            showSnackbar('Profile updated successfully', 'success');
        } catch (error) {
            console.error('Failed to update profile:', error);
            showSnackbar('Failed to update profile', 'error');
        }
    };

    const handlePasswordChange = async () => {
        if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
            showSnackbar('New passwords do not match', 'error');
            return;
        }

        try {
            await auth.changePassword({
                currentPassword: changePasswordForm.currentPassword,
                newPassword: changePasswordForm.newPassword,
            });
            setPasswordDialogOpen(false);
            setChangePasswordForm({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });
            showSnackbar('Password changed successfully', 'success');
        } catch (error) {
            console.error('Failed to change password:', error);
            showSnackbar('Failed to change password', 'error');
        }
    };

    const handleDeleteAccount = async () => {
        try {
            await auth.deleteAccount(deletePassword);
            showSnackbar('Account deleted successfully', 'success');
            logout();
        } catch (error) {
            console.error('Failed to delete account:', error);
            showSnackbar('Failed to delete account', 'error');
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    if (!profile) {
        return <Typography>Loading...</Typography>;
    }

    return (
        <>
            <Paper sx={{ p: 3, borderRadius: '15px', mb: 4 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} gutterBottom>
                            Profile
                        </Typography>
                        <Divider />
                    </Box>

                    {!isEditing ? (
                        // View Mode
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Name
                                </Typography>
                                <Typography>{profile.name}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Email
                                </Typography>
                                <Typography>{profile.email}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Phone
                                </Typography>
                                <Typography>{profile.phone || 'Not set'}</Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Username
                                </Typography>
                                <Typography>{profile.username}</Typography>
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" color="textSecondary">
                                    Member Since
                                </Typography>
                                <Typography>
                                    {new Date(profile.created_at).toLocaleDateString()}
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                onClick={() => setIsEditing(true)}
                                sx={{
                                    mr: 2,
                                    textTransform: 'none',
                                    borderRadius: '13px',
                                    mb: 1,
                                }}
                            >
                                Edit Profile
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setPasswordDialogOpen(true)}
                                sx={{
                                    mr: 2,
                                    textTransform: 'none',
                                    borderRadius: '13px',
                                    mb: 1,
                                }}
                            >
                                Change Password
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setDeleteAccountDialogOpen(true)}
                                sx={{
                                    mr: 2,
                                    textTransform: 'none',
                                    borderRadius: '13px',
                                }}
                            >
                                Delete Account
                            </Button>
                        </Box>
                    ) : (
                        // Edit Mode
                        <Box>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "12px",
                                        },
                                    }}
                                />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Email"
                                    type="email"
                                    value={editForm.email}
                                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "12px",
                                        },
                                    }}
                                />
                            </Box>
                            <Box sx={{ mb: 3 }}>
                                <TextField
                                    fullWidth
                                    label="Phone"
                                    value={editForm.phone}
                                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "12px",
                                        },
                                    }}
                                />
                            </Box>
                            <Button
                                variant="contained"
                                onClick={handleEditSubmit}
                                sx={{ mr: 2, borderRadius: '13px', mb: 1 }}
                            >
                                Save Changes
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditForm({
                                        name: profile.name,
                                        email: profile.email,
                                        phone: profile.phone || '',
                                    });
                                }}
                                sx={{ borderRadius: '13px' }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    )}
                </Stack>
            </Paper>

            {/* Change Password Dialog */}
            <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} sx={{ '& .MuiDialog-paper': { borderRadius: '17px' } }}>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            type="password"
                            label="Current Password"
                            value={changePasswordForm.currentPassword}
                            onChange={(e) => setChangePasswordForm({
                                ...changePasswordForm,
                                currentPassword: e.target.value
                            })}
                            sx={{
                                mb: 2,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            type="password"
                            label="New Password"
                            value={changePasswordForm.newPassword}
                            onChange={(e) => setChangePasswordForm({
                                ...changePasswordForm,
                                newPassword: e.target.value
                            })}
                            sx={{
                                mb: 2,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            type="password"
                            label="Confirm New Password"
                            value={changePasswordForm.confirmPassword}
                            onChange={(e) => setChangePasswordForm({
                                ...changePasswordForm,
                                confirmPassword: e.target.value
                            })}
                            sx={{
                                mb: 2,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "12px",
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPasswordDialogOpen(false)} sx={{ borderRadius: '13px', textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handlePasswordChange} variant="contained" sx={{ borderRadius: '13px', textTransform: 'none', py: 1.2 }} >
                        Change Password
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Account Dialog */}
            <Dialog open={deleteAccountDialogOpen} onClose={() => setDeleteAccountDialogOpen(false)}>
                <DialogTitle>Delete Account</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography sx={{ mb: 2 }} color="error">
                            Warning: This action cannot be undone. All your data will be permanently deleted.
                        </Typography>
                        <TextField
                            fullWidth
                            type="password"
                            label="Enter your password to confirm"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteAccountDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteAccount} variant="contained" color="error">
                        Delete Account
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};
